/**
 * GBP Store — wachtrij, goedkeuring en cijfers van het Google Bedrijfsprofiel-kanaal
 * (Marketing > Google Bedrijfsprofiel). Praat met /api/marketing/gbp/* via marketingFetch.
 */

import { create } from 'zustand';
import { marketingFetch } from './marketing-store';

export type GbpBrand = 'kleda' | 'cinco';
export type GbpSoort = 'review_reply' | 'post' | 'foto' | 'profiel_update' | 'qna_answer';
export type GbpStatus = 'concept' | 'goedgekeurd' | 'gepubliceerd' | 'geskipt' | 'afgekeurd' | 'mislukt';
export type GbpActieNaam = 'approve' | 'skip' | 'edit' | 'requeue';

export const GBP_SOORTEN: readonly GbpSoort[] = ['review_reply', 'post', 'foto', 'profiel_update'];
export const GBP_BRANDS: readonly GbpBrand[] = ['kleda', 'cinco'];

export interface GbpActie {
  id: string;
  brand: GbpBrand;
  soort: GbpSoort;
  status: GbpStatus;
  bron_ref: string;
  bron_detail: {
    sterren?: number;
    tekst?: string | null;
    auteur?: string | null;
    voornaam?: string | null;
    datum?: string | null;
    toon?: string;
    reden?: string;
    beeld_url?: string;
    bron_omschrijving?: string;
    cta_url?: string;
    category?: string;
    waarom?: string;
    veld?: string;
    oud?: unknown;
    nieuw?: unknown;
    fout?: string;
    opnieuw?: boolean;
  } | null;
  payload: Record<string, unknown>;
  preview: string | null;
  preview_response: { dry_run?: boolean; ok?: boolean; checks?: Record<string, unknown> } | null;
  extern_id: string | null;
  extern_url: string | null;
  gepubliceerd_op: string | null;
  actor_bron: 'brein' | 'office-app';
  beoordeeld_door: string | null;
  beoordeeld_at: string | null;
  beoordeel_reden: string | null;
  bewerkt_door: string | null;
  fout: string | null;
  pogingen: number;
  created_at: string;
}

export interface GbpStatsData {
  performance: Record<string, { d7: Record<string, number>; d28: Record<string, number>; vorige7: Record<string, number>; laatste_dag: string | null }>;
  antwoordgraad: Record<string, { totaal: number; beantwoord: number; pct: number; open_48u: number }>;
  wachtrij: Record<string, Record<string, number>>;
  autonoom: { mtd_gepubliceerd: number; mtd_brein: number; pct: number };
  fotos: Record<string, number>;
  posts30d: Record<string, number>;
  audit: Record<string, { run_at: string; score: number; detail: { onderdelen?: Record<string, { punten: number; max: number; toelichting?: string }>; acties?: string[] } } | undefined>;
  snapshot: Record<string, { dag: string; drift_google: Array<{ veld: string; oud: unknown; nieuw: unknown }> | null; drift_canon: Array<{ veld: string; live: unknown; canon: unknown }> | null; titel: string | null; categorieen: { primary: string; additional: string[] } | null; beschrijving: string | null; diensten: string[] | null } | undefined>;
}

interface GbpState {
  acties: GbpActie[];
  actiesStatus: GbpStatus | 'alle';
  soortFilter: GbpSoort | null;
  merkFilter: GbpBrand | null;
  stats: GbpStatsData | null;
  isLoading: boolean;
  error: string | null;

  fetchActies: (status?: GbpStatus | 'alle', soort?: GbpSoort | null, merk?: GbpBrand | null) => Promise<void>;
  act: (id: string, action: GbpActieNaam, payload?: { preview?: string; payload?: Record<string, unknown>; reden?: string }) => Promise<void>;
  fetchStats: () => Promise<void>;
}

export const useGbpStore = create<GbpState>((set, get) => ({
  acties: [],
  actiesStatus: 'concept',
  soortFilter: null,
  merkFilter: null,
  stats: null,
  isLoading: false,
  error: null,

  fetchActies: async (status, soort, merk) => {
    const s = status ?? get().actiesStatus;
    const so = soort === undefined ? get().soortFilter : soort;
    const m = merk === undefined ? get().merkFilter : merk;
    set({ isLoading: true, error: null, actiesStatus: s, soortFilter: so, merkFilter: m });
    try {
      const qs = `status=${encodeURIComponent(s)}&limit=100${so ? `&soort=${so}` : ''}${m ? `&merk=${m}` : ''}`;
      const data = await marketingFetch<{ acties: GbpActie[] }>(`/api/marketing/gbp/acties?${qs}`);
      set({ acties: data.acties, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Laden mislukt', isLoading: false });
    }
  },

  act: async (id, action, payload = {}) => {
    await marketingFetch('/api/marketing/gbp/acties', { method: 'POST', body: { id, action, ...payload } });
    if (action !== 'edit') {
      set({ acties: get().acties.filter((a) => a.id !== id) });
    } else {
      set({ acties: get().acties.map((a) => (a.id === id ? { ...a, preview: payload.preview ?? a.preview } : a)) });
    }
    void get().fetchActies();
    void get().fetchStats();
  },

  fetchStats: async () => {
    try {
      const stats = await marketingFetch<GbpStatsData>('/api/marketing/gbp/stats');
      set({ stats });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Statistieken laden mislukt' });
    }
  },
}));
